import { QueryReturnValue } from "@reduxjs/toolkit/dist/query/baseQueryTypes";
import { MaybePromise } from "@reduxjs/toolkit/dist/query/tsHelpers";
import { BaseQueryApi, createApi, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { AgentAssistConfig, NextBestActionRequest, NextBestActionResponse } from "../../model/nextBestAction";
import agentCopilotAccessSlice from "../agentCopilotAccessSlice";
import { agentCopilotApiBaseQuery, post } from "../helpers";
import { RootState } from "../store";

const agentCopilotApi = createApi({
  reducerPath: "agentCopilot",
  tagTypes: [],
  baseQuery: agentCopilotApiBaseQuery("/agentcopilot"),
  endpoints: builder => ({
    getNextBestAction: builder.query<NextBestActionResponse, { data: NextBestActionRequest }>({
      queryFn: queryFnWithCopilotIdArg(({ copilotId, data }) => post(`/copilots/${copilotId}/next-best-action`, data)),
    }),
    generateShortcutAnswer: builder.query<NextBestActionResponse, { data: { quickResponseId: string } & NextBestActionRequest }>({
      queryFn: queryFnWithCopilotIdArg(({ copilotId, data: { quickResponseId, ...data } }) => post(`/copilots/${copilotId}/shortcuts/${quickResponseId}/generate-response`, data)),
    }),
    getAgentAssistConfig: builder.query<AgentAssistConfig, void>({
      queryFn: queryFnWithCopilotIdArg(({ copilotId }) => `/copilots/${copilotId}/config`),
    }),
  }),
});

export const {
  useLazyGetNextBestActionQuery,
  useLazyGenerateShortcutAnswerQuery,
  useGetAgentAssistConfigQuery,
} = agentCopilotApi;

export default agentCopilotApi;

function queryFnWithCopilotIdArg<TArg extends Record<string, unknown> | void, TResponse>(query: (args: TArg & { copilotId: string }) => string | FetchArgs) {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  return (arg: TArg, { getState }: BaseQueryApi, __: {}, baseQuery: (arg: string | FetchArgs) => MaybePromise<QueryReturnValue<unknown, unknown, {}>>): MaybePromise<QueryReturnValue<TResponse, FetchBaseQueryError, unknown>> => {
    const { copilotId } = (getState() as RootState)[agentCopilotAccessSlice.name];

    if (!copilotId) {
      const error = { name: "ConditionError", message: "The copilotId could not be recovered from store." } as unknown as FetchBaseQueryError;
      return { error };
    }

    return baseQuery(query({ ...arg, copilotId })) as MaybePromise<QueryReturnValue<TResponse, FetchBaseQueryError, unknown>>;
  };
}
